import { useTranslation } from "react-i18next";
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  Children,
} from "react";
import clsx from "classnames";
import style from "./InputText.module.css";
import useAltEffect from "shared/hooks/useAltEffect";

import { CFormInput } from "@coreui/react";

const InputText = forwardRef((props, ref) => {
  const input = useRef();
  const {
    regExp,
    placeHolder,
    label,
    manualValid = false,
    style: extraStyle = {},
    targetRef,
    readOnly,
    numberOnly,
    onKeyDown,
    feedback = "pleaseCheck",
    validable = true,
    max,
  } = props;
  const { t } = useTranslation();
  const [name, setName] = useState(props.name || "");
  const [value, setValue] = useState(props.value || "");
  const [isValid, setValid] = useState(true);
  const [isEmpty, setEmpty] = useState(true);
  const [active, setActive] = useState(false);
  const [searchInterval, setSearchInterval] = useState();
  const onChange = (e) => {
    let inputValue = e.target.value;
    let onlyNumber = inputValue.replace(/\D/g, "");
    if (numberOnly) {
      inputValue = onlyNumber;
    }

    setValue(inputValue);
    if (e.target.value.length > 0) {
      setActive(true);
    } else {
      setActive(false);
    }
  };

  useAltEffect(() => {
    setValue(props.value || "");
  }, [props.value]);

  useEffect(() => {
    if (searchInterval) clearInterval(searchInterval);
    const interval = setInterval(async () => {
      if (!manualValid)
        if (regExp) {
          const validationResult = await regExp.reduce(async (acc, re) => {
            switch (re.exp.test.constructor.name) {
              case "AsyncFunction": {
                let eachValidationResult = await re.exp.test(value);
                if (eachValidationResult === false) {
                  return false;
                }
                break;
              }
              default: {
                let eachValidationResult = re.exp.test(value);
                if (eachValidationResult === false) {
                  return false;
                }
              }
            }
            return acc;
          }, true);
          setValid(validationResult);
        } else {
          setValid(true);
        }
      clearInterval(interval);
    }, 1000);
    setSearchInterval(interval);
    if (value && value.length > 0) {
      setEmpty(false);
    } else {
      setEmpty(true);
    }

    if (props.onChange) {
      props.onChange(value);
    }
  }, [value]);

  useImperativeHandle(ref, () => ({
    getName() {
      return name;
    },
    getValue() {
      return value;
    },
    setValue(value) {
      setValue(value);
    },
    isValid() {
      return isValid;
    },
    setValid(valid) {
      setValid(valid);
    },
    empty() {
      setValue("");
    },
    focus() {
      input.current.scrollTo(0, input.current.offsetTop);
      if (props.hidden) {
        targetRef.current.focus();
      } else {
        input.current.focus();
      }
    },
  }));

  const inputStyle = {
    border: "none",
    boxShadow: "none",
    borderRadius: "0",
    fontSize: "15px",
    padding: "0 20px",
    color: "var(--font-color)",
    height: "50px",
  };
  const defaultInputStyle = {
    "--cui-invalid-feedback": "none",
    boxShadow: "none",
    borderRadius: "0",
    fontSize: "15px",
    border: "1px solid var(--line-color)",
  };

  return (
    <>
      {props.lineText ? (
        <div
          className={clsx(style.wrap, {
            [style.active]: isValid && active ? !active : active,
          })}
        >
          <CFormInput
            style={{ ...inputStyle, ...extraStyle }}
            type="text"
            // feedback={isValid ? false : "잘 입력좀해주세요"}
            // placeholder={placeHolder}
            value={value}
            valid={
              validable ? (readOnly ? null : isEmpty ? null : isValid) : null
            }
            invalid={validable ? (isEmpty ? null : !isValid) : null}
            onChange={onChange}
            label={label}
            ref={input}
            size={props.size ? props.size : null}
            disabled={props.disabled ? props.disabled : null}
            readOnly={readOnly}
          />
          <p
            className={clsx(
              style.placeHolder,
              { [style.readOnly]: readOnly },
              { [style.active]: active },
              { [style.defaultPlaceHolder]: !active }
            )}
          >
            {props.placeHolder}
          </p>
        </div>
      ) : (
        <div className={style.wrap}>
          <CFormInput
            style={{ ...defaultInputStyle, ...extraStyle }}
            type={props.hidden ? "hidden" : "text"}
            feedback={isValid ? false : t(feedback)}
            placeholder={placeHolder}
            value={value}
            valid={validable ? (isEmpty ? null : isValid) : null}
            invalid={validable ? (isEmpty ? null : !isValid) : null}
            onChange={onChange}
            label={label}
            ref={input}
            size={props.size ? props.size : null}
            disabled={props.disabled ? props.disabled : null}
            readOnly={readOnly}
            onKeyDown={onKeyDown}
            maxLength={max}
          />
        </div>
      )}
    </>
  );
});

export default InputText;
